import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiUtils } from '../../lib/api'

export interface DataSource {
  id: string
  name: string
  connectorType: string
  config: Record<string, any>
  projectId: string
  organizationId: string
  createdAt: string
  updatedAt: string
  isActive: boolean
}

interface DataSourcesState {
  dataSources: DataSource[]
  currentProjectDataSources: DataSource[]
  isLoading: boolean
  isInitialized: boolean
  error: string | null
}

const initialState: DataSourcesState = {
  dataSources: [],
  currentProjectDataSources: [],
  isLoading: false,
  isInitialized: false,
  error: null,
}

// Async thunks
export const fetchAllDataSources = createAsyncThunk(
  'dataSources/fetchAllDataSources',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.get('/data-sources')
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch data sources')
    }
  }
)

export const fetchDataSources = createAsyncThunk(
  'dataSources/fetchDataSources',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.get('/data-sources')
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch data sources')
    }
  }
)

export const fetchDataSourcesByProject = createAsyncThunk(
  'dataSources/fetchDataSourcesByProject',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.get(`/data-sources?projectId=${projectId}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch project data sources')
    }
  }
)

export const createDataSource = createAsyncThunk(
  'dataSources/createDataSource',
  async (data: {
    name: string
    connectorType: string
    config: Record<string, any>
    projectId: string
  }, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.post('/data-sources', data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create data source')
    }
  }
)

export const updateDataSource = createAsyncThunk(
  'dataSources/updateDataSource',
  async ({ id, ...data }: {
    id: string
    name?: string
    config?: Record<string, any>
    isActive?: boolean
  }, { rejectWithValue }) => {
    try {
      const response = await apiUtils.backend.put(`/data-sources/${id}`, data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update data source')
    }
  }
)

export const deleteDataSource = createAsyncThunk(
  'dataSources/deleteDataSource',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiUtils.backend.delete(`/data-sources/${id}`)
      return id
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete data source')
    }
  }
)

const dataSourcesSlice = createSlice({
  name: 'dataSources',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    filterByCurrentProject: (state, action: PayloadAction<string | null>) => {
      const projectId = action.payload
      if (projectId && Array.isArray(state.dataSources)) {
        state.currentProjectDataSources = state.dataSources.filter(
          ds => ds.projectId === projectId && ds.isActive
        )
      } else {
        state.currentProjectDataSources = []
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all data sources (app initialization)
      .addCase(fetchAllDataSources.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAllDataSources.fulfilled, (state, action) => {
        state.isLoading = false
        state.isInitialized = true
        state.dataSources = action.payload
        // Filter for current project will be handled by component when project changes
      })
      .addCase(fetchAllDataSources.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Fetch all data sources (legacy)
      .addCase(fetchDataSources.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchDataSources.fulfilled, (state, action) => {
        state.isLoading = false
        state.dataSources = action.payload
      })
      .addCase(fetchDataSources.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Fetch data sources by project
      .addCase(fetchDataSourcesByProject.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchDataSourcesByProject.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentProjectDataSources = action.payload
      })
      .addCase(fetchDataSourcesByProject.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Create data source
      .addCase(createDataSource.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createDataSource.fulfilled, (state, action) => {
        state.isLoading = false
        state.dataSources = [action.payload, ...state.dataSources]
        // If it belongs to current project, add to current project data sources
        if (state.currentProjectDataSources.some(ds => ds.projectId === action.payload.projectId)) {
          state.currentProjectDataSources = [action.payload, ...state.currentProjectDataSources]
        }
      })
      .addCase(createDataSource.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Update data source
      .addCase(updateDataSource.fulfilled, (state, action) => {
        const index = state.dataSources.findIndex(ds => ds.id === action.payload.id)
        if (index !== -1) {
          state.dataSources[index] = action.payload
        }
        const currentIndex = state.currentProjectDataSources.findIndex(ds => ds.id === action.payload.id)
        if (currentIndex !== -1) {
          state.currentProjectDataSources[currentIndex] = action.payload
        }
      })
      .addCase(updateDataSource.rejected, (state, action) => {
        state.error = action.payload as string
      })
      
      // Delete data source
      .addCase(deleteDataSource.fulfilled, (state, action) => {
        state.dataSources = state.dataSources.filter(ds => ds.id !== action.payload)
        state.currentProjectDataSources = state.currentProjectDataSources.filter(ds => ds.id !== action.payload)
      })
      .addCase(deleteDataSource.rejected, (state, action) => {
        state.error = action.payload as string
      })
  },
})

export const { clearError, filterByCurrentProject } = dataSourcesSlice.actions
export default dataSourcesSlice.reducer